import { AnimatePresence, motion } from 'framer-motion';
import { useState, useRef } from 'react';
import styles from './BlockAnimation.module.css';
import {
  BlockFilledIcon,
  BlockOneSideFilledIcon,
  BlockOutlineIcon,
} from './BlockIcons';

const MotionBlockOneSideFilledIcon = motion(BlockOneSideFilledIcon);
const MotionBlockOutlineIcon = motion(BlockOutlineIcon);
const MotionBlockFilledIcon = motion(BlockFilledIcon);

const blockWidth = 24;
const currentBlockSize = 40;
const defaultBlockSize = 24;
const exitOffset = 50;
const leftOffsets = [0, 32, 64, 104, 160, 192, 224];
const currentBlockOffset = leftOffsets[3];
const containerWidth = leftOffsets[leftOffsets.length - 1] + blockWidth;

const initialBlocks = [
  { id: 1, type: 'full', slot: 0 },
  { id: 2, type: 'full', slot: 1 },
  { id: 3, type: 'full', slot: 2 },
  { id: 4, type: 'current', slot: 3 },
  { id: 5, type: 'empty', slot: 4 },
  { id: 6, type: 'empty', slot: 5 },
  { id: 7, type: 'empty', slot: 6 },
];

const getOpacity = (slot: number) => {
  if (slot === 0) return 0.25;
  if (slot === 1) return 0.5;
  if (slot === 2) return 1;
  if (slot === 3) return 1;
  if (slot === 4) return 1;
  if (slot === 5) return 0.5;
  if (slot === 6) return 0.25;

  return 1;
};

const variants = {
  full: (slot: number) => ({
    initial: {
      left: currentBlockOffset,
      y: 0,
      opacity: 1,
      width: currentBlockSize,
      height: currentBlockSize,
    },
    animate: {
      left: leftOffsets[slot],
      opacity: getOpacity(slot),
      y: 0,
      width: defaultBlockSize,
      height: defaultBlockSize,
    },
    exit: {
      left: leftOffsets[slot] - exitOffset,
      opacity: 0,
      width: defaultBlockSize,
      height: defaultBlockSize,
    },
  }),
  current: (slot: number) => ({
    initial: {
      left: leftOffsets[3 + 1],
      opacity: 0,
      width: defaultBlockSize,
      height: defaultBlockSize,
    },
    animate: {
      left: leftOffsets[slot],
      opacity: 1,
      width: currentBlockSize,
      height: currentBlockSize,
    },
  }),
  empty: (slot: number) => ({
    initial: {
      left: leftOffsets[slot] + exitOffset,
      opacity: 0,
      width: defaultBlockSize,
      height: defaultBlockSize,
    },
    animate: {
      left: leftOffsets[slot],
      opacity: getOpacity(slot),
      width: defaultBlockSize,
      height: defaultBlockSize,
    },
    exit: {
      left: currentBlockOffset,
      y: 0,
      opacity: 0,
      width: currentBlockSize,
      height: currentBlockSize,
      transition: {
        opacity: { duration: DURATION },
        y: { duration: DURATION },
        left: { duration: DURATION },
        width: { duration: DURATION },
        height: { duration: DURATION },
      },
    },
  }),
  exiting: (slot: number) => ({
    initial: {
      left: leftOffsets[slot],
      opacity: 1,
      width: defaultBlockSize,
      height: defaultBlockSize,
    },
    animate: {
      left: leftOffsets[slot],
      opacity: 1,
      width: defaultBlockSize,
      height: defaultBlockSize,
    },
    exit: {
      left: leftOffsets[slot] - exitOffset,
      opacity: 0,
      width: defaultBlockSize,
      height: defaultBlockSize,
    },
  }),
};

const DURATION = 0.4;
const FLASH_DURATION = 250;

const animationConfigs = {
  full: { transition: { duration: DURATION } },
  current: { transition: { duration: DURATION } },
  empty: { transition: { duration: DURATION } },
};

const iconTypeMap = {
  full: MotionBlockFilledIcon,
  current: MotionBlockOneSideFilledIcon,
  empty: MotionBlockOutlineIcon,
};

const getZIndex = (type: string) => {
  if (type === 'current') return 10;
  return 1;
};

const BlockAnimation = ({
  blocks,
  isFirstRender,
  isFlashing,
}: {
  blocks: typeof initialBlocks;
  isFirstRender: boolean;
  isFlashing: boolean;
}) => {
  return (
    <div>
      <div
        className={styles['container']}
        style={{
          position: 'relative',
          width: containerWidth,
          height: blockWidth,
        }}
      >
        <AnimatePresence>
          {blocks.map(
            (block: (typeof blocks)[number] & { customKey?: string }) => {
              const key = block.customKey || block.id;
              const IconComponent =
                iconTypeMap[block.type as keyof typeof iconTypeMap];

              return (
                <IconComponent
                  key={key}
                  custom={block.slot}
                  variants={variants[block.type as keyof typeof variants](
                    block.slot
                  )}
                  initial={isFirstRender ? false : 'initial'}
                  animate="animate"
                  exit="exit"
                  transition={
                    animationConfigs[
                      block.type as keyof typeof animationConfigs
                    ].transition
                  }
                  style={{
                    position: 'absolute',
                    zIndex: getZIndex(block.type),
                  }}
                  isFlashing={block.type === 'current' && isFlashing}
                />
              );
            }
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export const useBlockAnimation = () => {
  const [blocks, setBlocks] = useState(initialBlocks);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [isFlashing, setIsFlashing] = useState(false);
  const nextIdRef = useRef(initialBlocks.length + 1);

  const mintBlock = (remainingBlocks: number) => {
    setIsFlashing(true);

    setTimeout(() => {
      setIsFlashing(false);
      if (isFirstRender) setIsFirstRender(false);
      setBlocks((prev) => {
        const updated = prev
          .filter((b) => b.slot !== 0)
          .map((b) => ({ ...b, slot: b.slot - 1 }));

        updated.forEach((b) => {
          if (b.slot === 2 && b.type === 'current') b.type = 'full';
        });

        const index = updated.findIndex(
          (b) => b.slot >= 3 && b.type === 'empty'
        );
        if (index !== -1) {
          const targetSlot = updated[index].slot;
          updated.splice(index, 1);
          updated.splice(index, 0, {
            id: nextIdRef.current++,
            type: 'current',
            slot: targetSlot,
          });
        }

        if (remainingBlocks > 2) {
          updated.push({ id: nextIdRef.current++, type: 'empty', slot: 6 });
        }

        return updated;
      });
    }, FLASH_DURATION);
  };

  return { blocks, isFirstRender, isFlashing, mintBlock };
};

export default BlockAnimation;
